import { computed, inject, Injectable, signal } from '@angular/core';
import { Device } from '../../shared/models/device';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../shared/models/api-response';
import {
  Observable,
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  share,
  tap,
  timeout,
} from 'rxjs';
import { FiscalDay } from '../../shared/models/fiscal-day';
import { Receipt } from '../../shared/models/receipt';
import { PingResponse } from '../../shared/models/ping-response';
import { FiscalDayStatus } from '../../shared/models/fiscal-day-status';
import { DeviceConfig } from '../../shared/models/device-config';
import { FiscalDayCounter } from '../../shared/models/fiscal-day-counter';
import { TaxPayerInformation } from '../../shared/models/tax-payer-information';
import { OpenDayDTO } from '../../shared/models/open-day-dto';
import { RegisterDeviceResponse } from '../../shared/models/register-device-response';
import { ReceiptValidationError } from '../../shared/models/receipt-validation-error';
import { ReceiptValidationErrorDTO } from '../../shared/models/receipt-validation-error-dto';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  private baseUrl = `${environment.baseUrl}devices`;

  _http = inject(HttpClient);

  public currentDevice: BehaviorSubject<Device | null> =
    new BehaviorSubject<Device | null>(null);

  public currentFiscalDay: BehaviorSubject<FiscalDay | null> =
    new BehaviorSubject<FiscalDay | null>(null);

  public currentFiscalDayList: BehaviorSubject<FiscalDay[]> =
    new BehaviorSubject<FiscalDay[]>([]);

  public currentReceipt: BehaviorSubject<Receipt | null> =
    new BehaviorSubject<Receipt | null>(null);

  public currentReceiptList: BehaviorSubject<Receipt[]> = new BehaviorSubject<
    Receipt[]
  >([]);

  private currentDeviceList$ = signal<Device[]>([]);

  public currentDevices = computed(this.currentDeviceList$);

  fetchDeviceList(
    pageNumber: number,
    pageSize: number,
  ): Observable<ApiResponse<Device[]>> {
    console.log('DeviceService::fetchDeviceList');
    // build request get params
    let request = new HttpParams().set('size', pageSize.toString());
    if (pageNumber && pageNumber > 0) {
      request = request.set('page', pageNumber.toString());
    }

    // if (sort && sort != '') {
    //   request = request.set('sort', sort);
    // }

    // console.log(request);
    const url = `${environment.baseUrl}devices`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<Device[]>>(url, {
        headers: {
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        tap((response: ApiResponse<Device[]>) => {
          if (response.success) {
            this.currentDeviceList$.update(draft => {
              return [...response.data]; // Spread the array
            });
          }
        }),
      );
  }

  fetchDeviceFiscalDayList(
    deviceID: number,
  ): Observable<ApiResponse<FiscalDay[]>> {
    console.log('DeviceService::fetchDeviceFiscalDayList');
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<FiscalDay[]>>(url, {
        headers: {
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        tap((response: ApiResponse<FiscalDay[]>) => {
          if (response.success) {
            this.currentFiscalDayList.next(response.data);
          }
        }),
      );
  }

  addNewDevice(newDevice: Device): Observable<ApiResponse<Device>> {
    console.log('addNewDevice: ');

    const request = new HttpParams();
    const url = `${environment.baseUrl}devices`;
    console.log(`url : ${url}`);

    return this._http
      .post<ApiResponse<Device>>(url, newDevice, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      .pipe(
        share(),
        tap((response: ApiResponse<Device>) => {
          if (response.success) {
            // add the new device to top the list
            const savedDevice = response.data;
            console.dir(savedDevice);
            this.currentDeviceList$.update(draft => {
              return [savedDevice, ...draft];
            });
          }
        }),
      );
  }

  registerDevice(
    deviceID: number,
  ): Observable<ApiResponse<RegisterDeviceResponse>> {
    console.log('registerDevice: ', deviceID);

    const url = `${environment.baseUrl}zimra/${deviceID}/register-device`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<RegisterDeviceResponse>>(url, {
        headers: {
          Accept: 'application/json',
        },
      })
      .pipe(share());
  }

  renewDeviceCertificate(
    deviceID: number,
  ): Observable<ApiResponse<RegisterDeviceResponse>> {
    console.log('renewDeviceCertificate: ', deviceID);

    const url = `${environment.baseUrl}devices/${deviceID}/get-certificate`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<RegisterDeviceResponse>>(url, {
        headers: {
          Accept: 'application/json',
        },
      })
      .pipe(share());
  }

  saveDeviceDetails(updatedDevice: Device): Observable<ApiResponse<Device>> {
    console.log('saveDeviceDetails: ', updatedDevice);

    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    headers.append('Accept', 'application/json');

    const request = new HttpParams();
    // console.log(request);

    const url = `${environment.baseUrl}devices/${updatedDevice.deviceID}`;
    console.log(`url : ${url}`);

    return this._http
      .patch<ApiResponse<Device>>(url, updatedDevice, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        tap((response: ApiResponse<Device>) => {
          if (response.success) {
            const updatedDevice = response.data;
            this.currentDevice.next(updatedDevice);
            // look for device in the list and update it
            const currentDeviceList = this.currentDeviceList$();
            const index = currentDeviceList.findIndex(
              device => device.deviceID === updatedDevice.deviceID,
            );
            if (index >= 0) {
              currentDeviceList[index] = updatedDevice;
              console.log(`index ${index} Device updated:`, currentDeviceList);
              this.currentDeviceList$.update(draft => {
                draft[index] = updatedDevice;
                return draft;
              });
            }
          }
        }),
      );
  }

  fetchDeviceReceiptList(deviceID: number): Observable<ApiResponse<Receipt[]>> {
    console.log('DeviceService::fetchDeviceReceiptList');
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}devices/${deviceID}/receipts`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<Receipt[]>>(url, {
        headers: {
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        tap((response: ApiResponse<Receipt[]>) => {
          if (response.success) {
            this.currentReceiptList.next(response.data);
          }
        }),
      );
  }

  fetchDeviceValidationErrors(
    deviceID: number,
  ): Observable<ApiResponse<ReceiptValidationErrorDTO[]>> {
    console.log('DeviceService::fetchDeviceValidationErrors');
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}devices/${deviceID}/validation-errors`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<ReceiptValidationErrorDTO[]>>(url, {
        headers: {
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        // tap((response: ApiResponse<ReceiptValidationError[]>) => {
        //   if (response.success) {
        //     // this.currentReceiptList.next(response.data);
        //   }
        // }),
      );
  }

  pingDeviceWithID(deviceID: number): Observable<ApiResponse<PingResponse>> {
    console.log('DeviceService::pingDeviceWithID', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}devices/${deviceID}/ping`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<PingResponse>>(url, {
        headers: {
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.KEYUP_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
      );
  }

  fetchStatusForDeviceWithID(
    deviceID: number,
  ): Observable<ApiResponse<FiscalDayStatus>> {
    console.log('DeviceService::fetchStatusForDeviceWithID', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}/status`;
    console.log(`url : ${url}`);

    return this._http.get<ApiResponse<FiscalDayStatus>>(url, {
      headers: {
        // 'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      params: request,
    }); //
    // .pipe(
    //   share(),
    //   debounceTime(environment.DEFAULT_REQUEST_TIMEOUT),
    // )
  }

  fetchConfigForDeviceWithID(
    deviceID: number,
  ): Observable<ApiResponse<DeviceConfig>> {
    console.log('DeviceService::fetchConfigForDeviceWithID', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}devices/${deviceID}/get-config`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<DeviceConfig>>(url, {
        headers: {
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.KEYUP_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
      );
  }

  closeFiscalDayForDeviceWithID(
    deviceID: number,
  ): Observable<ApiResponse<DeviceConfig>> {
    console.log('DeviceService::closeFiscalDayForDeviceWithID', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}/close-day`;
    console.log(`url : ${url}`);

    return this._http
      .post<ApiResponse<DeviceConfig>>(
        url,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          params: request,
        },
      ) //
      .pipe(share(), debounceTime(environment.DEFAULT_REQUEST_TIMEOUT));
  }

  fetchFiscalDayErrorsForDeviceWithID(
    deviceID: number,
    fiscalDayNo: number,
  ): Observable<ApiResponse<string[]>> {
    console.log(
      'DeviceService::fetchFiscalDayErrorsForDeviceWithID',
      deviceID,
      fiscalDayNo,
    );
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}/errors/${fiscalDayNo}`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<string[]>>(url, {
        headers: {
          Accept: 'application/json',
          Timeout: `${environment.DEFAULT_REQUEST_TIMEOUT}`,
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.KEYUP_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
      );
  }

  fetchSummaryFiscalDayForDeviceWithID(
    deviceID: number,
    fiscalDayNo: number,
  ): Observable<ApiResponse<FiscalDayCounter[]>> {
    console.log(
      'DeviceService::closeFiscalDayForDeviceWithID',
      deviceID,
      fiscalDayNo,
    );
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}/summary/${fiscalDayNo}`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<FiscalDayCounter[]>>(url, {
        headers: {
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.DEFAULT_REQUEST_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
      );
  }

  fetchTaxPayerInformation(
    deviceID: number,
  ): Observable<ApiResponse<TaxPayerInformation>> {
    console.log('DeviceService::fetchTaxPayerInformation', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}zimra/${deviceID}/verify-tax-payer`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<TaxPayerInformation>>(url, {
        headers: {
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.KEYUP_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
      );
  }

  openFiscalDayForDeviceWithID(
    deviceID: number,
    openDayRequest: OpenDayDTO,
  ): Observable<ApiResponse<OpenDayDTO>> {
    console.log(
      'DeviceService::openFiscalDayForDeviceWithID',
      deviceID,
      openDayRequest,
    );
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}fiscal-days/${deviceID}/open-day`;
    console.log(`url : ${url}`);

    return this._http
      .post<ApiResponse<OpenDayDTO>>(url, openDayRequest, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(share(), debounceTime(environment.DEFAULT_REQUEST_TIMEOUT));
  }

  fetchDeviceDetails(deviceID: number): Observable<ApiResponse<Device>> {
    console.log('DeviceService::fetchDeviceDetails', deviceID);
    // build request get params
    const request = new HttpParams();
    const url = `${environment.baseUrl}devices/${deviceID}`;
    console.log(`url : ${url}`);

    return this._http
      .get<ApiResponse<Device>>(url, {
        headers: {
          // 'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        params: request,
      }) //
      .pipe(
        share(),
        distinctUntilChanged(),
        debounceTime(environment.KEYUP_TIMEOUT),
        timeout(environment.DEFAULT_REQUEST_TIMEOUT),
        tap((response: ApiResponse<Device>) => {
          if (response.success) {
            this.currentDevice.next(response.data);
          }
        }),
      );
  }
}
